/**
 * Kjør et boot-steg med timeout (#13438).
 *
 * Boot-stien awaiter nettverk (token-refresh) og IndexedDB (money-clip) før
 * appen kan vise noe. Begge kan henge evig: mobilnett/proxyer som svelger
 * requests, og kjente Safari-bugs der IndexedDB-requests aldri fyrer (page
 * restore, flere faner, SW-aktivitet). Promise.race med timeout gjør stegene
 * best effort — ved timeout fortsetter innlastingen med fallback-verdien.
 *
 * Hvert steg rapporteres som `dabih_boot_step`-event til RUM (jf. #13426) slik
 * at vi kan se hvor innlastingen stopper hos ekte brukere.
 */
import { recordEvent } from 'src/utilities/monitoring.js';

const TIMED_OUT = Symbol('timed-out');

export async function bootStep<T>(
  step: string,
  work: () => Promise<T>,
  options: { timeoutMs: number; fallback: T },
): Promise<T> {
  const startedAt = performance.now();
  const promise = work();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      promise,
      new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), options.timeoutMs);
      }),
    ]);
    const durationMs = Math.round(performance.now() - startedAt);
    if (result === TIMED_OUT) {
      // Svelg en eventuell sen rejection fra det hengende arbeidet så den
      // ikke blir en unhandled rejection etter at vi har gått videre.
      promise.catch(() => {});
      console.warn(`[boot] Step "${step}" timed out after ${options.timeoutMs} ms - continuing without it`);
      recordEvent('dabih_boot_step', { step, outcome: 'timeout', durationMs });
      return options.fallback;
    }
    recordEvent('dabih_boot_step', { step, outcome: 'ok', durationMs });
    return result;
  } catch (error) {
    recordEvent('dabih_boot_step', {
      step,
      outcome: 'error',
      durationMs: Math.round(performance.now() - startedAt),
    });
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
